/** Overflow strategy for the mobile header row.
 *
 *  The mobile main row hosts three regions — a leading pill cluster, the
 *  compact page title and a trailing pill cluster — and on a 375pt phone their
 *  natural widths routinely out-demand the row. Flexbox alone can't rescue it:
 *  the pills are 44pt touch targets that must not shrink, so the surplus has
 *  to leave the row entirely. This module decides which pills stay as pills
 *  and which collapse into a single sheet-backed overflow pill.
 *
 *  Widths are ESTIMATED from each action's shape rather than measured from the
 *  DOM. That keeps the decision a pure function of (action list, row width) so
 *  it can run during render with no measure → re-render → re-measure loop in a
 *  component that also drives scroll-linked title opacity. Every estimate
 *  rounds up, so a wrong guess costs one collapsed pill rather than a row that
 *  overflows the viewport. */

import type { MobileHeaderAction, MobileHeaderSubAction } from './types';

/** Icon-only glass pill — a 44pt circle (Apple Liquid Glass spec). */
export const HEADER_PILL_BASE = 44;
/** The `gap-1` between pills inside a cluster. */
export const HEADER_CLUSTER_GAP = 4;
/** Floor the fit keeps clear for the compact title. Below roughly this the
 *  title stops carrying enough of the page name to orient anyone, and the
 *  resource-library views have no large title to fall back on. */
export const COMPACT_TITLE_RESERVE = 88;

/** Fixed chrome around a pill's label: 4pt outer and 10pt inner padding, both
 *  sides. */
const LABEL_CHROME = 8 + 20;
/** A 17pt leading icon plus its 6pt gap. */
const LABEL_ICON = 23;
/** Upper bound on one 13pt-semibold character's advance. Deliberately past the
 *  real average so the fit errs toward collapsing a pill. */
const LABEL_CHAR_ADVANCE = 7.2;

/** A pill can only become a sheet row if there is something to invoke: its own
 *  onClick, or sub-actions to flatten. Pills that render a bespoke panel
 *  instead (the merchant category/brand filter, the native `<select>`
 *  switchers) carry neither, so they can never be collapsed. */
export function isCollapsibleAction(action: MobileHeaderAction): boolean {
  return !!action.onClick || (action.subActions?.length ?? 0) > 0;
}

export function estimateHeaderPillWidth(action: MobileHeaderAction): number {
  if (!action.label) return HEADER_PILL_BASE;
  const chrome = LABEL_CHROME + (action.icon ? LABEL_ICON : 0);
  return Math.max(
    HEADER_PILL_BASE,
    Math.ceil(chrome + action.label.length * LABEL_CHAR_ADVANCE),
  );
}

/** Width of a cluster: the pills plus the gaps flexbox draws *between* them. */
export function measureCluster(actions: MobileHeaderAction[]): number {
  if (actions.length === 0) return 0;
  return actions.reduce((total, action) => total + estimateHeaderPillWidth(action), 0)
    + (actions.length - 1) * HEADER_CLUSTER_GAP;
}

/** Stand-in for the overflow pill itself when measuring a candidate cluster —
 *  an icon-only circle, like the one the header renders. */
const OVERFLOW_PILL: MobileHeaderAction = { id: '', ariaLabel: '' };

/** Narrowest a cluster can ever be: the pills no fit can remove, plus the
 *  overflow pill it will need if it has anything left to collapse. Omitting
 *  that pill was worth 44pt of phantom budget — the other cluster spent it,
 *  then this one overran and the compact title absorbed the difference. */
export function measureClusterFloor(
  actions: MobileHeaderAction[],
  isPinned: (action: MobileHeaderAction) => boolean,
): number {
  const pinned = actions.filter(
    (action) => isPinned(action) || !isCollapsibleAction(action),
  );
  const hasCollapsible = pinned.length < actions.length;
  return measureCluster(hasCollapsible ? [...pinned, OVERFLOW_PILL] : pinned);
}

export interface ClusterFit {
  /** Pills that stay in the row, in their original order. */
  visible: MobileHeaderAction[];
  /** Pills the row can't afford — empty when everything fits. */
  collapsed: MobileHeaderAction[];
}

export function fitCluster(
  actions: MobileHeaderAction[],
  budget: number,
  isPinned: (action: MobileHeaderAction) => boolean,
): ClusterFit {
  const pinned = (action: MobileHeaderAction) =>
    isPinned(action) || !isCollapsibleAction(action);
  // Stable: with no overflowPriority set, this is array order, as before.
  const flexible = actions
    .filter((action) => !pinned(action))
    .map((action, index) => ({ action, index }))
    .sort((a, b) => (b.action.overflowPriority ?? 0) - (a.action.overflowPriority ?? 0) || a.index - b.index)
    .map(({ action }) => action);
  if (flexible.length === 0 || measureCluster(actions) <= budget) {
    return { visible: actions, collapsed: [] };
  }

  // An overflow pill is certain from here, so it claims width ahead of any pill
  // it might collapse.
  const pinnedList = actions.filter(pinned);
  const admitted: MobileHeaderAction[] = [];
  let cut = false;
  const collapsed: MobileHeaderAction[] = [];
  for (const action of flexible) {
    // Cut at the FIRST pill that doesn't fit and collapse everything after it.
    // Admitting a later, narrower pill past a dropped one would silently
    // reorder the row against the priority each builder encoded. Measured in
    // priority order; the cluster's width does not depend on order.
    const fits = measureCluster([...pinnedList, ...admitted, action, OVERFLOW_PILL]) <= budget;
    if (cut || !fits) {
      cut = true;
      collapsed.push(action);
      continue;
    }
    admitted.push(action);
  }

  const collapsedIds = new Set(collapsed.map((action) => action.id));
  return {
    visible: actions.filter((action) => !collapsedIds.has(action.id)),
    collapsed,
  };
}

/** Width a cluster occupies once fitted, overflow pill included. */
export function measureFittedCluster(fit: ClusterFit): number {
  return measureCluster(
    fit.collapsed.length > 0 ? [...fit.visible, OVERFLOW_PILL] : fit.visible,
  );
}

/** Turns collapsed pills into flat sheet rows. MobileHeaderSubAction has no
 *  nested subActions, so a collapsed pill that owned a menu (the
 *  NRM2/SMM7 grouping) emits its children carrying the parent's name as
 *  their sublabel rather than a second level the sheet can't render. */
export function flattenOverflowActions(
  actions: MobileHeaderAction[],
): MobileHeaderSubAction[] {
  return actions.flatMap((action): MobileHeaderSubAction[] => {
    const subActions = action.subActions ?? [];
    if (subActions.length > 0) {
      const parentName = action.label ?? action.ariaLabel;
      return subActions.map((sub) => ({
        ...sub,
        icon: sub.icon ?? action.icon,
        sublabel: sub.sublabel ?? parentName,
      }));
    }
    return [{
      id: action.id,
      icon: action.icon,
      label: action.label ?? action.ariaLabel,
      ariaLabel: action.ariaLabel,
      onClick: () => action.onClick?.(),
      active: action.active,
      disabled: action.disabled,
    }];
  });
}

/** Badges on collapsed pills would otherwise vanish with them, hiding that a
 *  filter is still active. */
export function sumOverflowBadges(actions: MobileHeaderAction[]): number {
  return actions.reduce((total, action) => total + (action.badge ?? 0), 0);
}

/** The overflow badge speaks for every count it hides, so it stays red unless
 *  all of them are routine. */
export function overflowBadgeTone(actions: MobileHeaderAction[]): MobileHeaderAction['badgeTone'] {
  const counted = actions.filter((action) => (action.badge ?? 0) > 0);
  return counted.length > 0 && counted.every((action) => action.badgeTone === 'brand') ? 'brand' : 'alert';
}
